/**
 * ManagementHubKit.js
 *
 * Small presentational building blocks shared by the Management hub / area /
 * settings screens so they all speak the "Happy Premium" language without
 * hand-rolling the same cards again:
 *
 *  - {@link HubIntro}      — calm white intro card (tinted icon chip, eyebrow, title, scope chip);
 *  - {@link ModuleTileGrid} — 2-up (3-up on tablets) {@link ActionTile} grid with locked states;
 *  - {@link HeroCta}       — white pill that lives in the footer of the orange `HeroKpiCard`;
 *  - {@link TabsBar}       — scrollable pill tabs in a white capsule (replaces wrapped chip rows);
 *  - {@link SearchField}   — pill search input with a brand focus ring and a clear button.
 *
 * Presentation only — callers own data, navigation and gating. Everything is
 * theme-aware through `useManagementTheme` (light + dark).
 *
 * @module components/management/ManagementHubKit
 */

import React, {useState} from 'react';
import {ActivityIndicator, Image, Pressable, StyleSheet, Text, TextInput, TouchableOpacity, View} from 'react-native';
import Icon from './SafeIcon';
import DemoModeToggleButton from '../DemoModeToggleButton';
import {ActionTile, IconChip, PillTabs} from '../premium/PremiumKit';
import {BRAND, useManagementTheme} from '../../helpers/managementTheme';
import {useManagementLayout} from '../../helpers/managementLayout';

/**
 * Calm white intro card for a hub / area screen that has no headline figure of
 * its own (so no orange hero): tinted icon chip (or the store photo), eyebrow,
 * title, subtitle and an optional scope chip. The self-gating Demo toggle that
 * used to ride on the gradient hero stays available in the top-right corner.
 *
 * @param {object} props
 * @param {string} [props.icon] MDI glyph for the chip (ignored when `imageUri` is set).
 * @param {string} [props.tone='primary'] Chip tone.
 * @param {string} [props.eyebrow] Small orange label above the title.
 * @param {string} props.title Big heading.
 * @param {string} [props.subtitle] Muted description under the title.
 * @param {string} [props.imageUri] Photo shown instead of the glyph chip.
 * @param {string} [props.scopeLabel] Store / place chip text.
 * @param {string} [props.scopeIcon='store-marker-outline'] Glyph of the scope chip.
 * @param {Array<{label: string, icon?: string}>} [props.badges] Extra tinted pills under the title.
 * @param {React.ReactNode} [props.right] Extra action(s) next to the Demo toggle.
 * @param {Function} [props.onIconPress] Makes the icon chip tappable.
 * @returns {JSX.Element} The intro card.
 */
export const HubIntro = ({
    icon,
    tone = 'primary',
    eyebrow,
    title,
    subtitle,
    imageUri,
    scopeLabel,
    scopeIcon = 'store-marker-outline',
    badges,
    right,
    onIconPress,
}) => {
    const m = useManagementTheme();
    const lead = imageUri
        ? <Image source={{uri: imageUri}} style={styles.introImage} resizeMode="cover" />
        : <IconChip icon={icon || 'view-grid-outline'} tone={tone} size={56} />;
    const chips = Array.isArray(badges) ? badges.filter((badge) => badge && badge.label) : [];

    return (
        <View style={[styles.intro, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <View style={styles.introTop}>
                {onIconPress ? (
                    <Pressable
                        onPress={onIconPress}
                        accessibilityRole="button"
                        style={({pressed}) => ({opacity: pressed ? 0.8 : 1})}
                    >
                        {lead}
                    </Pressable>
                ) : lead}
                <View style={styles.introText}>
                    {!!eyebrow && <Text style={[styles.introEyebrow, {color: m.colors.primary}]} numberOfLines={1}>{eyebrow}</Text>}
                    <Text style={[styles.introTitle, {color: m.colors.heading}]} numberOfLines={2}>{title}</Text>
                    {!!subtitle && <Text style={[styles.introSub, {color: m.colors.subText}]} numberOfLines={3}>{subtitle}</Text>}
                </View>
                <View style={styles.introRight}>
                    <DemoModeToggleButton color={m.colors.subText} />
                    {right}
                </View>
            </View>

            {(!!scopeLabel || chips.length > 0) && (
                <View style={styles.introChips}>
                    {!!scopeLabel && (
                        <View style={[styles.pill, {backgroundColor: m.colors.chipBg}]}>
                            <Icon name={scopeIcon} size={14} color={m.colors.primary} />
                            <Text style={[styles.pillText, {color: m.colors.chipText}]} numberOfLines={1}>{scopeLabel}</Text>
                        </View>
                    )}
                    {chips.map((badge) => (
                        <View key={badge.label} style={[styles.pill, {backgroundColor: m.colors.surfaceAlt}]}>
                            {!!badge.icon && <Icon name={badge.icon} size={14} color={m.colors.subText} />}
                            <Text style={[styles.pillText, {color: m.colors.subText}]} numberOfLines={1}>{badge.label}</Text>
                        </View>
                    ))}
                </View>
            )}
        </View>
    );
};

/**
 * Module / shortcut tiles laid out two-up on phones and three-up on tablets.
 * Each tile keeps its own gating: `disabled` renders the muted locked tile with
 * its `lockedReason`, and a locked tile never fires `onPress`.
 *
 * @param {object} props
 * @param {Array<{key: string, icon: string, tone?: string, title: string, subtitle?: string, disabled?: boolean, lockedReason?: string, onPress?: Function}>} props.items Tiles, in render order.
 * @returns {JSX.Element} The grid.
 */
export const ModuleTileGrid = ({items = []}) => {
    const {isWide} = useManagementLayout();

    return (
        <View style={[styles.tileGrid, isWide ? styles.tileGridWide : styles.tileGridPhone]}>
            {items.map((item) => (
                <ActionTile
                    key={item.key}
                    width={isWide ? '31.5%' : '48.5%'}
                    icon={item.icon}
                    tone={item.tone}
                    title={item.title}
                    subtitle={item.subtitle}
                    disabled={!!item.disabled}
                    lockedReason={item.lockedReason}
                    onPress={item.onPress}
                />
            ))}
        </View>
    );
};

/**
 * White pill button for the footer of the orange `HeroKpiCard` — the screen's
 * primary action ("New role", "New template"…) sits inside the highlight card.
 *
 * @param {object} props
 * @param {string} props.label Button text.
 * @param {string} [props.icon='plus'] MDI glyph.
 * @param {Function} props.onPress Tap handler.
 * @param {boolean} [props.disabled=false] Greys the button out.
 * @param {boolean} [props.loading=false] Shows a spinner instead of the glyph.
 * @returns {JSX.Element} The pill.
 */
export const HeroCta = ({label, icon = 'plus', onPress, disabled = false, loading = false}) => (
    <Pressable
        onPress={onPress}
        disabled={disabled || loading}
        accessibilityRole="button"
        accessibilityState={{disabled: disabled || loading}}
        style={({pressed}) => [
            styles.heroCta,
            {opacity: disabled ? 0.55 : (pressed ? 0.9 : 1)},
            pressed && !disabled && {transform: [{scale: 0.98}]},
        ]}
    >
        {loading ? <ActivityIndicator size="small" color={BRAND.base} /> : <Icon name={icon} size={18} color={BRAND.base} />}
        <Text style={styles.heroCtaText} numberOfLines={1}>{label}</Text>
    </Pressable>
);

/**
 * Horizontally scrollable pill tabs inside a white capsule. Replaces the
 * wrapped chip rows the ops screens used for their tab strips.
 *
 * @param {object} props
 * @param {Array<{value: *, label: string, icon?: string}>} props.options Tabs.
 * @param {*} props.value Active tab value.
 * @param {Function} props.onChange `(value) => void`.
 * @returns {JSX.Element} The tab bar.
 */
export const TabsBar = ({options = [], value, onChange}) => {
    const m = useManagementTheme();

    return (
        <View style={[styles.tabsBar, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <PillTabs options={options} value={value} onChange={onChange} variant="solid" scroll />
        </View>
    );
};

/**
 * Pill search input — brand-coloured focus ring, magnifier and a clear button.
 *
 * @param {object} props
 * @param {string} props.value Current text.
 * @param {Function} props.onChangeText Text handler.
 * @param {string} [props.placeholder] Placeholder text.
 * @param {Function} [props.onSubmitEditing] Submit handler.
 * @param {string} [props.returnKeyType='search'] Keyboard return key.
 * @param {string} [props.clearLabel] Accessibility label of the clear button.
 * @param {boolean} [props.inset=false] Flat variant for use inside a card (tinted fill, no shadow).
 * @param {boolean} [props.editable=true] Set `false` to lock the input (the clear button hides too).
 * @param {object} [props.style] Extra container style.
 * @returns {JSX.Element} The field.
 */
export const SearchField = ({
    value,
    onChangeText,
    placeholder,
    onSubmitEditing,
    returnKeyType = 'search',
    clearLabel,
    inset = false,
    editable = true,
    style,
}) => {
    const m = useManagementTheme();
    const [focused, setFocused] = useState(false);

    return (
        <View
            style={[
                styles.search,
                {
                    backgroundColor: inset ? m.colors.surfaceAlt : m.colors.surface,
                    borderColor: focused ? m.colors.primary : (inset ? m.colors.borderSoft : m.colors.border),
                },
                !inset && m.shadow(1),
                style,
            ]}
        >
            <Icon name="magnify" size={20} color={focused ? m.colors.primary : m.colors.muted} />
            <TextInput
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
                placeholderTextColor={m.colors.placeholder}
                returnKeyType={returnKeyType}
                onSubmitEditing={onSubmitEditing}
                editable={editable}
                autoCapitalize="none"
                autoCorrect={false}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                style={[styles.searchInput, {color: m.colors.heading}]}
            />
            {!!value && editable && (
                <TouchableOpacity
                    onPress={() => onChangeText?.('')}
                    hitSlop={8}
                    accessibilityRole="button"
                    accessibilityLabel={clearLabel}
                >
                    <Icon name="close-circle" size={18} color={m.colors.muted} />
                </TouchableOpacity>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    intro: {borderRadius: 26, borderWidth: 1, padding: 16, marginBottom: 16},
    introTop: {flexDirection: 'row', alignItems: 'center', gap: 14},
    introImage: {width: 56, height: 56, borderRadius: 19},
    introText: {flex: 1, minWidth: 0},
    introEyebrow: {fontSize: 11.5, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.8},
    introTitle: {fontSize: 21, fontWeight: '800', letterSpacing: -0.5, marginTop: 2},
    introSub: {fontSize: 13.5, fontWeight: '500', lineHeight: 19, marginTop: 4},
    introRight: {flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start'},
    introChips: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14},
    pill: {flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, paddingHorizontal: 12, paddingVertical: 7, maxWidth: '100%'},
    pillText: {fontSize: 12.5, fontWeight: '700', flexShrink: 1},

    tileGrid: {flexDirection: 'row', flexWrap: 'wrap'},
    tileGridPhone: {justifyContent: 'space-between'},
    tileGridWide: {justifyContent: 'flex-start', columnGap: 14},

    heroCta: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 48, borderRadius: 999, backgroundColor: '#FFFFFF', marginTop: 16},
    heroCtaText: {color: BRAND.deep, fontSize: 15, fontWeight: '800', flexShrink: 1},

    tabsBar: {borderRadius: 999, borderWidth: 1, padding: 5, marginBottom: 14},

    search: {flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 18, paddingHorizontal: 14, paddingVertical: 12, marginBottom: 14},
    searchInput: {flex: 1, fontSize: 15, fontWeight: '600', padding: 0},
});
